import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { homeService, BannerData } from "../services";
import defaultImage from "../assets/images/cuisines/default.jpeg";

const TopCard = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const res: any = await homeService.getBanners();
        if (res.status && Array.isArray(res.data)) {
          setBanners(
            [...res.data].sort(
              (a: BannerData, b: BannerData) => a.sortOrder - b.sortOrder
            )
          );
        }
      } catch (error) {
        console.error("Failed to fetch banners:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = window.setInterval(() => {
      setCurrentPage((page) => (page + 1) % banners.length);
    }, 3000);
    return () => window.clearInterval(interval);
  }, [banners.length]);

  const handleBannerPress = (banner: BannerData) => {
    switch (banner.actionType) {
      case "restaurant": {
        const restaurantId =
          banner.actionData?.restaurantDetails?._id || banner.actionData?._id;
        if (restaurantId) {
          navigate(`/restaurants/${restaurantId}`);
        }
        break;
      }
      case "category":
      case "cuisine":
        navigate(
          `/result?keyword=${encodeURIComponent(
            banner.actionData?.categoryDetails?.name ||
              banner.actionData?.cuisineTypeDetails?.name ||
              banner.title
          )}`
        );
        break;
      case "external": {
        const url = (banner.actionData as { url?: string } | undefined)?.url;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        break;
      }
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="w-full my-[10px] md:my-5">
        <div className="w-full h-[160px] md:h-[200px] rounded-[18px] bg-gray-200 dark:bg-gray-700 animate-pulse" />
      </div>
    );
  }

  if (banners.length === 0) {
    return null;
  }

  return (
    <div className="w-full my-[10px] md:my-5">
      <div className="relative w-full h-[160px] md:h-[200px] rounded-[18px] overflow-hidden">
        {banners.map((banner, index) => (
          <button
            key={banner._id}
            type="button"
            onClick={() => handleBannerPress(banner)}
            className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${
              index === currentPage
                ? "opacity-100 z-10"
                : "opacity-0 z-0 pointer-events-none"
            }`}
            aria-label={banner.title || "Banner"}
          >
            <img
              src={imageErrors.has(banner._id) ? defaultImage : banner.image}
              alt={banner.title || "Banner"}
              className="w-full h-full object-cover"
              onError={() =>
                setImageErrors((prev) => new Set(prev).add(banner._id))
              }
            />
          </button>
        ))}
      </div>

      {banners.length > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-2.5">
          {banners.map((banner, index) => (
            <button
              key={banner._id}
              type="button"
              onClick={() => setCurrentPage(index)}
              className={`h-[7px] rounded-full transition-all ${
                index === currentPage
                  ? "w-4 bg-[#7402A7]"
                  : "w-[7px] bg-[#050505] opacity-30"
              }`}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TopCard;
